// LeadershipPositions.jsx
import React from 'react';
import { Users, DollarSign, Heart, Shield, Briefcase } from 'lucide-react';
import "./LeadershipPositions.css";

const LeadershipPositions = ({ data, onChange, validationErrors = {} }) => {
  const handleMultiSelect = (field, value) => {
    const currentValues = data[field] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    onChange(field, newValues);
  };

  const handleJobPositionChange = (index, value) => {
    const positions = data.leadershipPositions || Array(7).fill('');
    positions[index] = value;
    onChange('leadershipPositions', positions);
  };

  const handleOtherFieldChange = (field, value) => {
    onChange(field, value);
  };

  // Helper function to get error class
  const getErrorClass = (fieldName) => {
    return validationErrors[fieldName] ? 'error' : '';
  };

  // Helper function to render error message
  const renderError = (fieldName) => {
    if (validationErrors[fieldName]) {
      return (
        <div className="error-message" role="alert">
          {validationErrors[fieldName]}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="step-content">
      <div className="step-header-section">
        {/* <Users className="section-icon" size={32} /> */}
        <h2>ثالثاً: البيانات الشخصية المتصلة بالوظائف القيادية، والمتخصصة، والإدارية، ووظائف العمالة الماهرة</h2>
      </div>
      
      {/* Leadership Positions List */}
      <div className="">
        <div className="question-header">
          <span className="question-number">12</span>
          <label className="question-title">اذكر مسمى الوظائف القيادية الموجودة في شركتكم؟</label>
        </div>
        
        <div className="positions-grid">
          {[0, 1, 2, 3, 4, 5, 6].map(index => (
            <div key={index} className="position-input-group">
              <label className="position-label" htmlFor={`leadership-position-${index}`}>
                الوظيفة {String(index + 1).padStart(2, '0')}:
              </label>
              <input
                id={`leadership-position-${index}`}
                type="text"
                value={(data.leadershipPositions || [])[index] || ''}
                onChange={(e) => handleJobPositionChange(index, e.target.value)}
                placeholder="اسم الوظيفة القيادية"
                className="position-input"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Personal Data Requirements */}
      <div className="">
        <div className="question-header">
          <span className="question-number">13</span>
          <label className="question-title">
            حدد البيانات الشخصية التي تطلبها شركتكم من المترشحين للتعيين للوظائف القيادية؟ 
            <span className="required-indicator">*</span>
            <span className="multiple-choice-note">(يمكن اختيار أكثر من إجابة)</span>
          </label>
        </div>
        
        {/* Financial Data Section */}
        <div className="data-category" data-field="leadershipFinancialData">
          <div className="category-header">
            <DollarSign className="category-icon" size={24} />
            <h4>البيانات المالية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('leadershipFinancialData')}`}>
            {[
              'الراتب السابق',
              'الحالة الائتمانية',
              'حسابات البنوك',
              'بيانات التأمينات الاجتماعية',
              'مصادر الدخل الأخرى',
              'الاستثمارات والممتلكات',
              'الالتزامات المالية والديون',
              'غير موجود'
            ].map(item => (
              <label key={item} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={(data.leadershipFinancialData || []).includes(item)}
                  onChange={() => handleMultiSelect('leadershipFinancialData', item)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{item}</span>
              </label>
            ))}
          </div>
          {renderError('leadershipFinancialData')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-financial">
              أخرى (يرجى ذكرها):
            </label>
            <input
              id="other-financial"
              type="text"
              value={data.otherLeadershipFinancialData || ''}
              onChange={(e) => handleOtherFieldChange('otherLeadershipFinancialData', e.target.value)}
              placeholder="بيانات مالية أخرى..."
              className={`other-input ${getErrorClass('otherLeadershipFinancialData')}`}
            />
            {renderError('otherLeadershipFinancialData')}
          </div>
        </div>

        {/* Health Data Section */}
        <div className="data-category" data-field="leadershipHealthData">
          <div className="category-header">
            <Heart className="category-icon" size={24} />
            <h4>البيانات الصحية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('leadershipHealthData')}`}>
            {[
              'التقارير الطبية العامة',
              'فحوصات طبية متخصصة',
              'تاريخ الأمراض المزمنة',
              'الاحتياجات الخاصة',
              'التطعيمات (كوفيد-19، وغيرها)',
              'غير موجود'
            ].map(item => (
              <label key={item} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={(data.leadershipHealthData || []).includes(item)}
                  onChange={() => handleMultiSelect('leadershipHealthData', item)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{item}</span>
              </label>
            ))}
          </div>
          {renderError('leadershipHealthData')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-health">
              أخرى (يرجى ذكرها):
            </label>
            <input
              id="other-health"
              type="text"
              value={data.otherLeadershipHealthData || ''}
              onChange={(e) => handleOtherFieldChange('otherLeadershipHealthData', e.target.value)}
              placeholder="بيانات صحية أخرى..."
              className={`other-input ${getErrorClass('otherLeadershipHealthData')}`}
            />
            {renderError('otherLeadershipHealthData')}
          </div>
        </div>

        {/* Legal and Security Data Section */}
        <div className="data-category" data-field="leadershipLegalData">
          <div className="category-header">
            <Shield className="category-icon" size={24} />
            <h4>البيانات القانونية والأمنية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('leadershipLegalData')}`}>
            {[
              'قضايا قانونية سابقة',
              'تصاريح أمنية سابقة',
              'السجل الجنائي المفصل',
              'التحقيقات الأمنية',
              'غير موجود'
            ].map(item => (
              <label key={item} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={(data.leadershipLegalData || []).includes(item)}
                  onChange={() => handleMultiSelect('leadershipLegalData', item)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{item}</span>
              </label>
            ))}
          </div>
          {renderError('leadershipLegalData')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-legal">
              أخرى (يرجى ذكرها):
            </label>
            <input
              id="other-legal"
              type="text"
              value={data.otherLeadershipLegalData || ''}
              onChange={(e) => handleOtherFieldChange('otherLeadershipLegalData', e.target.value)}
              placeholder="بيانات قانونية وأمنية أخرى..."
              className={`other-input ${getErrorClass('otherLeadershipLegalData')}`}
            />
            {renderError('otherLeadershipLegalData')}
          </div>
        </div>

        {/* Advanced Professional Data Section */}
        <div className="data-category" data-field="leadershipProfessionalData">
          <div className="category-header">
            <Briefcase className="category-icon" size={24} />
            <h4>البيانات المهنية المتقدمة:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('leadershipProfessionalData')}`}>
            {[
              'تقييمات الأداء السابقة',
              'أسباب ترك الوظائف',
              'غير موجود'
            ].map(item => (
              <label key={item} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={(data.leadershipProfessionalData || []).includes(item)}
                  onChange={() => handleMultiSelect('leadershipProfessionalData', item)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{item}</span>
              </label>
            ))}
          </div>
          {renderError('leadershipProfessionalData')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-professional">
              أخرى (يرجى ذكرها):
            </label>
            <input
              id="other-professional"
              type="text"
              value={data.otherLeadershipProfessionalData || ''}
              onChange={(e) => handleOtherFieldChange('otherLeadershipProfessionalData', e.target.value)}
              placeholder="بيانات مهنية أخرى..."
              className={`other-input ${getErrorClass('otherLeadershipProfessionalData')}`}
            />
            {renderError('otherLeadershipProfessionalData')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadershipPositions;